import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useToast } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useWallet } from '../hooks/queries';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ui/ThemeToggle';

/** Compact daily-energy pill; links to Play where energy is spent. */
const EnergyPill: React.FC = () => {
  const { data: wallet } = useWallet();
  if (!wallet) return null;
  const empty = wallet.energy < 1;
  return (
    <Link
      to="/play"
      className={`glass hidden items-center gap-1 rounded-full px-2.5 py-1.5 text-sm font-bold tabular-nums transition-colors sm:flex ${
        empty ? 'text-ink-faint' : 'text-warning'
      }`}
      title={
        empty
          ? 'Out of AI quiz energy — refills at midnight UTC'
          : `${wallet.energy} of ${wallet.cap} AI quizzes left today`
      }
    >
      <span aria-hidden className={empty ? 'grayscale' : ''}>⚡</span>
      {wallet.energy}
    </Link>
  );
};

const Logo: React.FC = () => (
  <Link to="/" className="flex items-center gap-2.5">
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-accent to-accent-2 text-base font-extrabold text-white shadow-[0_2px_12px_color-mix(in_oklab,var(--accent)_45%,transparent)]">
      m
    </span>
    <span className="text-xl font-bold tracking-tight text-ink">
      mod<span className="text-gradient">LRN</span>
    </span>
  </Link>
);

interface NavItem {
  path: string;
  label: string;
}

const authedItems: NavItem[] = [
  { path: '/dashboard', label: 'Home' },
  { path: '/friends', label: 'Friends' },
  { path: '/challenges', label: 'Challenges' },
  { path: '/leaderboards', label: 'Leaderboards' },
  { path: '/create', label: 'Create' },
];

const Navbar: React.FC = () => {
  const toast = useToast();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // Close the profile menu on outside click
  useEffect(() => {
    if (!profileOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [profileOpen]);

  // Close menus on navigation
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } finally {
      toast.success('Signed out', 'See you next time!');
      navigate('/login', { replace: true });
    }
  }, [logout, navigate, toast]);

  const isActive = (path: string) => location.pathname === path;
  const items = user ? authedItems : [];

  return (
    <motion.nav
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-4"
    >
      <div className="glass-strong mx-auto flex h-14 max-w-7xl items-center justify-between rounded-2xl px-4">
        <Logo />

        {/* Desktop nav */}
        <div className="hidden items-center gap-1 md:flex">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-accent-soft text-accent-strong'
                  : 'text-ink-soft hover:bg-accent-soft/40 hover:text-ink'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          {user && <EnergyPill />}
          <ThemeToggle />
          {user && <NotificationBell />}

          {user ? (
            <div ref={profileRef} className="relative">
              <button
                onClick={() => setProfileOpen((o) => !o)}
                className="flex items-center gap-2 rounded-full p-0.5 transition-transform hover:scale-105"
                aria-label="Open profile menu"
                aria-expanded={profileOpen}
              >
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover ring-2 ring-accent/40"
                  />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft text-sm font-bold text-accent-strong ring-2 ring-accent/40">
                    {(user.name || user.username || user.email || '?')[0].toUpperCase()}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {profileOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="glass-menu absolute right-0 mt-2 w-56 overflow-hidden rounded-xl2 py-1.5"
                  >
                    <div className="border-b border-edge/60 px-4 py-2.5">
                      <p className="truncate text-sm font-semibold text-ink">
                        {user.name || user.username || 'Player'}
                      </p>
                      <p className="truncate text-xs text-ink-faint">{user.email}</p>
                    </div>
                    {[
                      { to: '/profile', label: 'Profile & trophies' },
                      { to: '/settings', label: 'Settings' },
                    ].map((entry) => (
                      <Link
                        key={entry.to}
                        to={entry.to}
                        className="block px-4 py-2 text-sm text-ink-soft transition-colors hover:bg-accent-soft/40 hover:text-ink"
                      >
                        {entry.label}
                      </Link>
                    ))}
                    <button
                      onClick={handleLogout}
                      className="block w-full px-4 py-2 text-left text-sm text-danger transition-colors hover:bg-danger/10"
                    >
                      Sign out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="hidden items-center gap-2 md:flex">
              <Link
                to="/login"
                className="rounded-lg px-3.5 py-1.5 text-sm font-medium text-ink-soft transition-colors hover:text-ink"
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className="rounded-xl bg-gradient-to-b from-accent to-accent-strong px-4 py-2 text-sm font-semibold text-white shadow-[0_2px_12px_color-mix(in_oklab,var(--accent)_45%,transparent)] transition-[filter] hover:brightness-110"
              >
                Play free
              </Link>
            </div>
          )}

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="rounded-lg p-2 text-ink-soft transition-colors hover:text-ink md:hidden"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              {menuOpen ? (
                <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="mx-auto mt-2 max-w-7xl overflow-hidden md:hidden"
          >
            <div className="glass-strong space-y-1 rounded-2xl p-2">
              {(user ? items : [{ path: '/login', label: 'Log in' }, { path: '/signup', label: 'Play free' }]).map(
                (item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-accent-soft text-accent-strong'
                        : 'text-ink-soft hover:bg-accent-soft/40 hover:text-ink'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default memo(Navbar);
